import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import staticURL from 'lib/staticURL';
import {
  StructurePredictionModel,
  MAX_AMINO_ACID_COUNT,
  MULTIMER_ENABLED_MODELS,
  getModelDisplayName,
} from 'molbio/proteins/util/StructurePredictionConstants';
import {getDefaultStructurePredictionModel} from 'molbio/proteins/util/StructurePredictionHelpers';
import notifications from 'notifications';
import {FONT_PROXIMA_13, FONT_PROXIMA_17} from 'styles/FontConstants';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Spinner from 'ui-library/Spinner';

import {RemainingExecutionsAndTenantNameQuery} from '../__generated/RemainingExecutionsAndTenantNameQuery';
import {UserVerificationStatusQuery} from '../__generated/UserVerificationStatusQuery';
import {AlphaFoldPaperLink} from '../AlphaFoldPaperLink';
import {
  getRunBlockingMessage,
  REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY,
  USER_VERIFICATION_STATUS,
  requestRun,
} from '../AlphaFoldTabUtils';
import ModelSelector from '../ModelSelector';
import ComponentSelector from './ComponentSelector';
import {flattenComponentSelectionToComponentIds, SelectedComponents} from './ComponentUtils';
import StickyFooter, {STICKY_FOOTER_CONTAINER_STYLES, STICKY_FOOTER_CONTENT_STYLES} from './StickyFooter';

export default function NewPredictionForm({
  aminoAcids,
  permissions,
  proteinId,
  onSubmit,
  onBack,
  onRunAllModels,
}: {
  aminoAcids: string;
  permissions: Permissions;
  proteinId: string;
  onSubmit: () => void;
  onBack: () => void;
  onRunAllModels?: () => Promise<void>;
}): JSX.Element {
  const remainingExecutionsAndTenantNameQueryResult =
    useBenchlingQuery<RemainingExecutionsAndTenantNameQuery>(REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY);
  const userVerificationStatusQueryResult =
    useBenchlingQuery<UserVerificationStatusQuery>(USER_VERIFICATION_STATUS);

  const [selectedModel, setSelectedModel] = useState<StructurePredictionModel>(
    getDefaultStructurePredictionModel()
  );
  const [selectedComponents, setSelectedComponents] = useState<SelectedComponents>({
    [proteinId]: {
      aminoAcids,
      count: 1,
      numHeavyAtoms: 0,
      entityType: ModelTypeConstants.PROTEIN,
    },
  });
  const [awaitingResponse, setAwaitingResponse] = useState<boolean>(false);

  // Update and re-validate primary protein's amino acids when they change in the store
  useEffect(() => {
    setSelectedComponents((prevComponents) => {
      // Only update if the primary protein exists in components and amino acids have changed
      if (prevComponents[proteinId] && prevComponents[proteinId].aminoAcids !== aminoAcids) {
        return {
          ...prevComponents,
          [proteinId]: {
            ...prevComponents[proteinId],
            aminoAcids,
          },
        };
      }
      return prevComponents;
    });
  }, [aminoAcids, proteinId]);

  const showModelSelector = BenchlingConfig.MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS;
  const showRunAllModels = BenchlingConfig.MODEL_HUB_ENABLE_RUN_ALL_MODELS;

  if (
    remainingExecutionsAndTenantNameQueryResult.type === QueryResultType.PENDING ||
    userVerificationStatusQueryResult.type === QueryResultType.PENDING
  ) {
    return <CenteredHorizontalThrobber />;
  } else if (
    remainingExecutionsAndTenantNameQueryResult.type === QueryResultType.ERROR ||
    userVerificationStatusQueryResult.type === QueryResultType.ERROR
  ) {
    notifications.error('Error when loading structure prediction details. Please refresh the page.');
    return <></>;
  }

  const currentUser = userVerificationStatusQueryResult.data.currentUser;
  assert(
    currentUser?.__typename === 'UserNode' || currentUser == null,
    `Non-user should not access this page.`
  );

  let blockingBannerMessage: string | null = null;
  if (
    BenchlingConfig.MOLBIO_ALPHAFOLD_IS_ACADEMIC &&
    !(
      // (A) Valid academic academic user
      (
        (currentUser?.institution?.verified && currentUser?.verified) ||
        // (B) Valid trial industry user. We hackily use MOLBIO_ALPHAFOLD_IS_ACADEMIC for per-user limits.
        // These users should always be verified before using the tenant, so failing this check is very
        // unexpected, to the point that we don't bother tailoring the banner message.
        (BenchlingConfig.LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT && currentUser?.verified)
      )
    )
  ) {
    blockingBannerMessage =
      'Structure prediction is only available to verified academic users from verified institutions.';
  } else {
    blockingBannerMessage = getRunBlockingMessage(
      aminoAcids,
      permissions,
      remainingExecutionsAndTenantNameQueryResult.data.remainingAlphafoldExecutions
    );
  }

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.content)}>
        {showModelSelector && showRunAllModels && onRunAllModels && !blockingBannerMessage && (
          <div className={css(styles.largeInlineBannerMargin)}>
            <InlineBanner
              bannerType={InlineBannerType.INFO}
              hideIcon={true}
              text="Not sure which model to run? Compare them by running all 3."
              action={{
                actionType: InlineBannerActionType.CUSTOM,
                actionText: awaitingResponse ? 'Submitting...' : 'Run All',
                actionOnClick: async () => {
                  if (awaitingResponse) {
                    return;
                  }
                  setAwaitingResponse(true);
                  try {
                    await onRunAllModels();
                  } finally {
                    setAwaitingResponse(false);
                    onSubmit();
                  }
                },
                buttonStyle: ButtonStyle.PRIMARY,
                rightAlign: true,
              }}
            />
          </div>
        )}
        {blockingBannerMessage && (
          <div className={css(styles.largeInlineBannerMargin)}>
            <InlineBanner text={blockingBannerMessage} bannerType={InlineBannerType.WARNING} />
          </div>
        )}
        <div className={css(styles.centered, styles.imgMargin)}>
          <img
            width={255}
            height={175}
            src={staticURL('/static/img/alphafold_example_quality_structure.png')}
          />
        </div>
        <p className={css(styles.largestText, styles.centered)}>Predict 3D structure</p>
        <p className={css(styles.smallerText, styles.grayDarker, styles.centered)}>
          Predict structures of proteins up to {MAX_AMINO_ACID_COUNT} residues using&nbsp;
          {showModelSelector ? getModelDisplayName(selectedModel) : <AlphaFoldPaperLink />}
        </p>

        {showModelSelector && (
          <div className={css(styles.centered, styles.modelSelectorContainer)}>
            <ModelSelector
              selectedModel={selectedModel}
              onModelChange={(model) => {
                setSelectedModel(model);
                // When switching models, reset to only the primary protein with count 1
                setSelectedComponents({
                  [proteinId]: {
                    aminoAcids,
                    count: 1,
                    numHeavyAtoms: 0,
                    entityType: ModelTypeConstants.PROTEIN,
                  },
                });
              }}
              disabled={blockingBannerMessage != null}
              className={css(styles.modelSelector)}
            />
          </div>
        )}

        {showModelSelector && MULTIMER_ENABLED_MODELS.includes(selectedModel) && (
          <ComponentSelector
            selectedComponents={selectedComponents}
            onComponentsChange={setSelectedComponents}
            primaryProteinId={proteinId}
            disabled={blockingBannerMessage != null}
          />
        )}
      </div>
      <StickyFooter>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MEDIUM}
          text="Cancel"
          onClick={onBack}
          disabled={awaitingResponse}
        />
        <Button
          disabled={blockingBannerMessage != null || awaitingResponse}
          onClick={async () => {
            setAwaitingResponse(true);
            try {
              const {proteinIds, moleculeIds} = flattenComponentSelectionToComponentIds(selectedComponents);
              await requestRun(
                proteinIds,
                moleculeIds,
                proteinId,
                remainingExecutionsAndTenantNameQueryResult.data,
                selectedModel
              );
              trackEvent(MOLBIO_ANALYTICS_ACTIONS.REQUESTED_ALPHAFOLD_RUN);
            } finally {
              setAwaitingResponse(false);
              onSubmit();
            }
          }}
          buttonStyle={ButtonStyle.SECONDARY}
          size={ButtonSize.MEDIUM}
          className={css(styles.submitButton)}
        >
          {awaitingResponse ? (
            <div className={css(styles.buttonContent)}>
              <Spinner />
              <span>Submitting...</span>
            </div>
          ) : (
            'Predict 3D structure'
          )}
        </Button>
      </StickyFooter>
    </div>
  );
}

const styles = StyleSheet.create({
  grayDarker: {
    color: GRAY_DARKER,
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
  },
  largestText: FONT_PROXIMA_17,
  smallerText: FONT_PROXIMA_13,
  imgMargin: {
    margin: '34px 0px',
  },
  modelSelectorContainer: {
    margin: '20px 0px',
  },
  modelSelector: {
    minWidth: '400px',
    textAlign: 'left',
    padding: '0px 16px',
  },
  largeInlineBannerMargin: {
    margin: '18px 11px',
  },
  submitButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  buttonSpinner: {
    width: '16px',
    height: '16px',
  },
  container: STICKY_FOOTER_CONTAINER_STYLES,
  content: {
    ...STICKY_FOOTER_CONTENT_STYLES,
    minWidth: '400px',
  },
});
